"use client";
import React from "react";
import { usePathname } from "next/navigation";
import {
  Cog8ToothIcon,
  ArrowDownOnSquareStackIcon,
  DocumentChartBarIcon,
  DocumentTextIcon,
  DocumentCheckIcon,
  Squares2X2Icon,
  RectangleStackIcon,
  QueueListIcon,
  IdentificationIcon,
  UserGroupIcon,
  BuildingStorefrontIcon,
  MagnifyingGlassCircleIcon,
  MegaphoneIcon,
  RocketLaunchIcon,
  CubeIcon,
  BanknotesIcon,
  GlobeAsiaAustraliaIcon,
  ArrowsRightLeftIcon,
  ArrowPathIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";
import {
  Cog8ToothIcon as Cog8ToothIconSolid,
  ArrowDownOnSquareStackIcon as ArrowDownOnSquareStackIconSolid,
  DocumentChartBarIcon as DocumentChartBarIconSolid,
  DocumentCheckIcon as DocumentCheckIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  Squares2X2Icon as Squares2X2IconSolid,
  RectangleStackIcon as RectangleStackIconSolid,
  QueueListIcon as QueueListIconSolid,
  IdentificationIcon as IdentificationIconSolid,
  UserGroupIcon as UserGroupIconSolid,
  BuildingStorefrontIcon as BuildingStorefrontIconSolid,
  MagnifyingGlassCircleIcon as MagnifyingGlassCircleIconSolid,
  MegaphoneIcon as MegaphoneIconSolid,
  RocketLaunchIcon as RocketLaunchIconSolid,
  CubeIcon as CubeIconSolid,
  BanknotesIcon as BanknotesIconSolid,
  GlobeAsiaAustraliaIcon as GlobeAsiaAustraliaIconSolid,
  ArrowsRightLeftIcon as ArrowsRightLeftIconSolid,
  ArrowPathIcon as ArrowPathIconSolid,
  GlobeAltIcon as GlobeAltIconSolid,
} from "@heroicons/react/24/solid";
import {
  CurrencyNairaIcon,
  CurrencyNairaIconSolid,
} from "../../molecules/icons";
import { SidebarItem, SidebarLabel } from "../../atoms/sidebar";
import {
  Count,
  DropdownSeparator,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  NavbarItem,
  NavbarSection,
} from "@/components/atoms";
import {
  AcademicCapIcon,
  ArrowRightStartOnRectangleIcon,
  BriefcaseIcon,
  UserIcon,
} from "@heroicons/react/16/solid";
import { FaWhatsapp } from "react-icons/fa6";
import { LifebuoyIcon } from "@heroicons/react/20/solid";
import { signOut } from "next-auth/react";
import { useShowGetStartedNav } from "./get-started-nav";

const GET_STARTED_ROUTE = "/procure/get-started";

const MenuItem = ({
  item,
}: {
  item: {
    name: string;
    route: string;
    parent: string | undefined;
    icon: any;
    count?: number;
    disabled?: boolean;
  };
}) => {
  const pathname = usePathname();
  const parent = pathname?.startsWith("/procure")
    ? pathname?.split("/")[2]
    : pathname?.split("/")[1];

  return (
    <SidebarItem
      href={item.disabled ? "#" : item.route}
      current={parent === item.parent}>
      {item.disabled ? (
        <item.icon.solid className="text-opacity-100 !size-5 text-gray-400 !hover:text-gray-400" />
      ) : (
        <item.icon.solid className="text-zinc-500 !size-5" />
      )}
      <SidebarLabel
        className={`text-base md:text-[15px] font-medium ${item.disabled ? "text-opacity-100 text-gray-400 cursor-not-allowed" : ""}`}>
        {item.name}
      </SidebarLabel>
      {item.count && <Count count={0} active={false} />}
    </SidebarItem>
  );
};

export default function MenuListSidebar() {
  const pathname = usePathname();
  const showGetStarted = useShowGetStartedNav();
  const menuList =
    pathname?.startsWith("/procure") || pathname.endsWith("/")
      ? [
          {
            items: [
              {
                name: "Get Started",
                route: GET_STARTED_ROUTE,
                parent: "get-started",
                icon: {
                  outline: RocketLaunchIcon,
                  solid: RocketLaunchIconSolid,
                },
              },
              {
                name: "Dashboard",
                route: "/procure",
                parent: undefined,
                icon: {
                  outline: Squares2X2Icon,
                  solid: Squares2X2IconSolid,
                },
              },
            ],
          },
          {
            title: "Sourcing",
            items: [
              {
                name: "Imports",
                route: "/procure/imports",
                parent: "imports",
                icon: {
                  outline: ArrowDownOnSquareStackIcon,
                  solid: ArrowDownOnSquareStackIconSolid,
                },
                disabled: false,
              },
              {
                name: "Requests",
                route: "/procure/requests",
                parent: "requests",
                icon: {
                  outline: MagnifyingGlassCircleIcon,
                  solid: MagnifyingGlassCircleIconSolid,
                },
                disabled: false,
              },
              // {
              //   name: "Quotes",
              //   route: "/procure/quotes",
              //   parent: "quotes",
              //   icon: {
              //     outline: DocumentChartBarIcon,
              //     solid: DocumentChartBarIconSolid,
              //   },
              //   disabled: false,
              // },
              {
                name: "Orders",
                route: "/procure/orders",
                parent: "orders",
                icon: {
                  outline: CubeIcon,
                  solid: CubeIconSolid,
                },
                disabled: false,
              },
              {
                name: "Shipments",
                route: "/procure/shipments",
                parent: "shipments",
                icon: {
                  outline: GlobeAsiaAustraliaIcon,
                  solid: GlobeAsiaAustraliaIconSolid,
                },
                disabled: false,
              },
            ],
          },
          {
            title: "Campaigns",
            items: [
              {
                name: "Campaigns",
                route: "/procure/campaigns/discover",
                parent: "campaigns",
                icon: {
                  outline: MegaphoneIcon,
                  solid: MegaphoneIconSolid,
                },
                disabled: false,
              },
            ],
          },
          {
            title: "Finance",
            items: [
              {
                name: "Wallet",
                route: "/procure/wallet",
                parent: "wallet",
                icon: {
                  outline: BanknotesIcon,
                  solid: BanknotesIconSolid,
                },
                disabled: false,
              },
            ],
          },
          {
            title: "",
            items: [
              {
                name: "Settings",
                route: "/settings/user",
                parent: `${pathname == "/settings/payments" ? "null" : "settings"}`,
                icon: {
                  outline: Cog8ToothIcon,
                  solid: Cog8ToothIconSolid,
                },
              },
            ],
          },
        ]
      : [
          {
            items: [
              {
                name: "Dashboard",
                route: "/",
                parent: "",
                icon: {
                  outline: Squares2X2Icon,
                  solid: Squares2X2IconSolid,
                },
              },
              {
                name: "Contacts",
                route: "/contacts",
                parent: "contacts",
                icon: {
                  outline: IdentificationIcon,
                  solid: IdentificationIconSolid,
                },
              },
            ],
          },
          {
            title: "Finance",
            items: [
              {
                name: "Invoices",
                route: "/invoices",
                parent: "invoices",
                icon: {
                  outline: DocumentChartBarIcon,
                  solid: DocumentChartBarIconSolid,
                },
              },
              {
                name: "Payments",
                route: "/payments",
                parent: "payments",
                icon: {
                  outline: CurrencyNairaIcon,
                  solid: CurrencyNairaIconSolid,
                },
              },
            ],
          },
          {
            title: "Inventory",
            items: [
              {
                name: "Categories",
                route: "/categories",
                parent: "categories",
                icon: {
                  outline: RectangleStackIcon,
                  solid: RectangleStackIconSolid,
                },
              },
              {
                name: "Products",
                route: "/products",
                parent: "products",
                icon: {
                  outline: QueueListIcon,
                  solid: QueueListIconSolid,
                },
              },
              {
                name: "Purchase Orders",
                route: "/purchase-orders",
                parent: "purchase-orders",
                icon: {
                  outline: DocumentCheckIcon,
                  solid: DocumentCheckIconSolid,
                },
              },
            ],
          },
          {
            title: "Marketplace",
            items: [
              {
                name: "Marketplace",
                route: "/marketplace",
                parent: "marketplace",
                icon: {
                  outline: UserGroupIcon,
                  solid: UserGroupIconSolid,
                },
                disabled: false,
              },
              {
                name: "Storefront",
                route: "/storefront",
                parent: "storefront",
                icon: {
                  outline: BuildingStorefrontIcon,
                  solid: BuildingStorefrontIconSolid,
                },
              },
            ],
          },
          {
            title: "",
            items: [
              {
                name: "Switch to Centi Procure",
                route: "/procure",
                parent: `procure`,
                icon: {
                  outline: ArrowPathIcon,
                  solid: ArrowPathIconSolid,
                },
              },
              {
                name: "Settings",
                route: "/settings/user",
                parent: `${pathname == "/settings/payments" ? "null" : "settings"}`,
                icon: {
                  outline: Cog8ToothIcon,
                  solid: Cog8ToothIconSolid,
                },
              },
            ],
          },
        ];

  const title =
    "ml-2.5 tracking-[1px] uppercase text-[10px] font-medium text-opacity-100 text-gray-400";

  return (
    <nav className="h-full flex flex-col justify-start px-5">
      <div className="flex flex-col space-y-1">
        {menuList[0].items
          .filter((item) => showGetStarted || item.route !== GET_STARTED_ROUTE)
          .map((item) => (
            <MenuItem key={item.name} item={item} />
          ))}
      </div>
      {menuList.slice(1, menuList.length - 1).map((menu) => (
        <div key={menu.title} className="flex flex-col space-y-1">
          <p className={`${title} pt-3 hidden`}>{menu.title}</p>
          <div className="flex flex-col space-y-1">
            {menu.items.map((item) => (
              <MenuItem key={item.name} item={item} />
            ))}
          </div>
        </div>
      ))}
      {
        <div className="flex-grow content-end pb-4">
          <div className="flex flex-col space-y-1">
            {menuList[menuList.length - 1].items.map((item) => (
              <MenuItem key={item.name} item={item} />
            ))}
          </div>
        </div>
      }
    </nav>
  );
}

export function MenuListStacked() {
  const pathname = usePathname();
  const showGetStarted = useShowGetStartedNav();
  const menu =
    pathname.includes("/procure") || pathname.endsWith("/")
      ? [
          { name: "Get Started", url: GET_STARTED_ROUTE },
          { name: "Dashboard", url: "/procure", alt: "/" },
          { name: "Imports", url: "/procure/imports" },
          { name: "Requests", url: "/procure/requests" },
          // { name: "Quotes", url: "/procure/quotes" },
          { name: "Orders", url: "/procure/orders" },
          { name: "Shipments", url: "/procure/shipments" },
          { name: "Campaigns", url: "/procure/campaigns/discover" },
          { name: "Wallet", url: "/procure/wallet" },
        ]
      : [
          { name: "Dashboard", url: "/hub" },
          {
            name: "Contacts",
            url: "/contacts",
          },
          {
            name: "Invoices",
            url: "/invoices",
          },
          {
            name: "Purchase Orders",
            url: "/purchase-orders",
          },
          {
            name: "Payments",
            url: "/payments",
          },
          {
            name: "Marketplace",
            url: "/marketplace",
          },
        ];

  return (
    <NavbarSection className="hidden lg:flex !gap-4">
      {menu
        .filter(({ url }) => showGetStarted || url !== GET_STARTED_ROUTE)
        .map(({ name, url, alt }) => (
          <NavbarItem
            large={false}
            key={name}
            href={url}
            current={
              pathname === url ||
              pathname === alt ||
              pathname.includes(name.toLowerCase())
            }
            active={
              pathname === url ||
              pathname === alt ||
              pathname.includes(name.toLowerCase())
            }>
            {name}
          </NavbarItem>
        ))}
    </NavbarSection>
  );
}

export function MenuListSecondary() {
  const pathname = usePathname();
  const menu = [
    { name: "My profile", url: "/procure/settings/user", icon: UserIcon },
    {
      name: "My business",
      url: "/procure/settings/business",
      icon: BriefcaseIcon,
    },
  ];
  return (
    <DropdownMenu className="min-w-64 z-[11]" anchor="bottom end">
      {menu.map((item, index) => (
        <DropdownItem
          key={index}
          href={item.url}
          current={pathname === item.url}>
          <item.icon />
          <DropdownLabel>{item.name}</DropdownLabel>
        </DropdownItem>
      ))}
      <DropdownSeparator />
      <DropdownItem
        href="/procure/learn"
        current={pathname.startsWith("/procure/learn")}>
        <AcademicCapIcon />
        <DropdownLabel>Learning &amp; Resources</DropdownLabel>
      </DropdownItem>
      {/* <DropdownItem href={pathname.includes("/procure") ? "/" : "/procure"}>
        <ArrowPathIconSolid />
        <DropdownLabel>
          Go to {pathname.includes("/procure") ? "Hub" : "Procure"}
        </DropdownLabel>
      </DropdownItem> */}
      <DropdownItem href="https://wa.link/rxphgx" target="_blank">
        <LifebuoyIcon />
        <DropdownLabel>Get support</DropdownLabel>
      </DropdownItem>
      <DropdownItem
        onClick={() =>
          signOut({
            redirectTo: "/login",
            redirect: true,
          })
        }>
        <ArrowRightStartOnRectangleIcon />
        <DropdownLabel>Sign out</DropdownLabel>
      </DropdownItem>
    </DropdownMenu>
  );
}
